import React, {ReactElement} from "react"
import {AllowedIncomingWiresColumns, IncomingWiresColumns} from "./IncomingWires.types"
import {Resource} from "../../resources/common"
import {useNavigate} from "react-router-dom"
import {BankName} from "../BankName/BankName"
import {Org} from "../../resources/org"
import {IncomingWire} from "../../resources/incomingWire"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import {useIsUnitUser} from "../../services/auth"
import {kebabCase, startCase} from "lodash"
import numeral from "numeral"
import moment from "moment"
import {extractDataFromFromImadOmad} from "../../utilities/helpers"
import ReactTooltip from "react-tooltip"
import classNames from "classnames"
import IdLink from "../IdLink/IdLink"

interface IncomingWireRowProps {
	incomingWire: IncomingWire
	includedColumns: AllowedIncomingWiresColumns[]
	refresh: () => void
	include?: Resource[]
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

function OrgName({orgId, included}: {orgId: number | undefined | null; included?: Resource[]}) {
	if (included && orgId) {
		const org = included.find((r) => r.type == "org" && r.id === orgId.toString()) as Org
		if (org) {
			return <td>{org.attributes.name}</td>
		}
	}
	return <td></td>
}

export function IncomingWireRow({incomingWire, includedColumns, include, onClick}: IncomingWireRowProps) {
	const navigate = useNavigate()
	const id = incomingWire.id
	const org = incomingWire.relationships.org?.data.id
	const bank = incomingWire.relationships.bank.data.id
	const customerId = incomingWire.relationships.customer?.data.id
	const accountId = incomingWire.relationships.account?.data.id
	const status = incomingWire.attributes.status
	const createdAt = incomingWire.attributes.createdAt
	const amount = incomingWire.attributes.amount
	const businessFunctionCode = incomingWire.attributes.businessFunctionCode
	const isMonetary = incomingWire.attributes.isMonetary
	const omad = incomingWire.attributes.incomingMessage.omad
	const dataFromOmad = omad && extractDataFromFromImadOmad(omad)

	const customer =
		customerId && include
			? include.find(
					(r) => (r.type == "individualCustomer" || r.type == "businessCustomer") && r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedIncomingWiresColumns, ReactElement>> = {}

	if (includedColumns.includes(IncomingWiresColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell>
				<IdLink href={`/incoming-wires/${id}`} className="data-table-id">
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.org)) {
		contentColumns["Org"] = <OrgName orgId={org} included={include} />
	}

	if (useIsUnitUser() && includedColumns.includes(IncomingWiresColumns.bank) && bank) {
		contentColumns["Bank"] = (
			<DataTableCell>
				<BankName bankId={bank.toString()} included={include} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="incoming-wires-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.status)) {
		const tooltipValue = undefined

		contentColumns["Status"] = (
			<DataTableCell>
				<div
					data-tip="React-tooltip"
					data-for={`status-reason-tooltip-${id}`}
					className={classNames("incoming-wire-status", kebabCase(status))}
				>
					{startCase(status)}

					<ReactTooltip
						className="tooltip-info"
						id={`status-reason-tooltip-${id}`}
						disable={!tooltipValue}
						effect="solid"
					>
						{tooltipValue}
					</ReactTooltip>
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(IncomingWiresColumns.businessFunctionCode)) {
		contentColumns["Business Function Code"] = <DataTableCell>{businessFunctionCode}</DataTableCell>
	}

	if (includedColumns.includes(IncomingWiresColumns.isMonetary)) {
		contentColumns["Monetary"] = <DataTableCell>{isMonetary ? "yes" : "no"}</DataTableCell>
	}

	if (includedColumns.includes(IncomingWiresColumns.cycleDate)) {
		contentColumns["Cycle Date"] = <DataTableCell>{dataFromOmad ? dataFromOmad.cycleDate : ""}</DataTableCell>
	}

	if (includedColumns.includes(IncomingWiresColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col, i) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return <DataTableRow onClick={(e) => onClick && onClick(e)}>{content}</DataTableRow>
}
